import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Polygon,
  Polyline,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { boundaryRings, flattenBoundaryRings, boundaryPolygonPaths, boundaryAcres } from "../utils/fieldBoundaryGeometry";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import PolylineIcon from "@mui/icons-material/Polyline";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { createFreehandStroke } from "../utils/freehandBoundary.js";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import { geocodeAddress } from "./googleMapsGeocoder";
import { resolvePreferredMapCenter } from "../utils/fieldMapAuthority";
import {
  findClosestProjectedSegment,
  removeVertexAtIndex,
} from "../utils/polygonEditing";
import { formatAcres } from "../utils/acreageFormatting.js";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const DEFAULT_CENTER = {
  lat: 57.534346,
  lng: -101.925502,
};

const GOOGLE_MAP_LIBRARIES = ["geometry", "places"];

const SAVED_POLYGON_OPTIONS = {
  fillColor: "#2563eb",
  fillOpacity: 0.15,
  strokeColor: "#2563eb",
  strokeOpacity: 1,
  strokeWeight: 3,
};

const DRAWING_POLYGON_OPTIONS = {
  fillColor: "#F15D22",
  fillOpacity: 0.25,
  strokeColor: "#F15D22",
  strokeOpacity: 1,
  strokeWeight: 2,
};

const DRAWING_POLYLINE_OPTIONS = {
  strokeColor: "#F15D22",
  strokeOpacity: 1,
  strokeWeight: 2,
};

const REFERENCE_POLYGON_OPTIONS = {
  fillColor: "#BF00FF",
  fillOpacity: 0.14,
  strokeColor: "#BF00FF",
  strokeOpacity: 0.95,
  strokeWeight: 2,
};

const CONFLICT_POLYGON_OPTIONS = {
  fillColor: "#DC2626",
  fillOpacity: 0.2,
  strokeColor: "#DC2626",
  strokeOpacity: 1,
  strokeWeight: 2,
};

const MapComponent = ({
  GOOGLE_APIKEY,
  address,
  drawFieldBoundaries,
  isTypingAddress,
  searchLocation,
  fieldState,
  setFieldState,
  onSaveBoundaries,
  savingBoundaries = false,
  onImportBoundary,
  onBoundaryToolbarReady,
  importDisabled = false,
  referenceFarms = [],
  overlapConflicts = [],
  fieldKey = fieldState?.id || "new",
}) => {
  const [map, setMap] = useState(null);
  const [activeRingKey, setActiveRingKey] = useState("0:0");
  const [ringMenuAnchor, setRingMenuAnchor] = useState(null);
  const [tracing, setTracing] = useState(false);
  const rings = boundaryRings(fieldState?.boundaries || []);
  const [activePolygon, activeRing] = activeRingKey.split(":").map(Number);
  const path = rings.find((ring) => ring.polygon === activePolygon && ring.ring === activeRing)?.points || [];
  const [drawing, setDrawing] = useState(false);
  const [hoveredReferenceField, setHoveredReferenceField] = useState(null);

  const [strokePreview, setStrokePreview] = useState([]);
  const [editError, setEditError] = useState("");
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [undoCount, setUndoCount] = useState(0);
  const fieldRef = useRef(fieldState);
  const ringKeyRef = useRef(activeRingKey);
  const stroke = useRef(null);
  const undoStack = useRef([]);
  const handleDrag = useRef(null);
  const hoverTimer = useRef(null);
  const freshTrace = useRef(false);
  const lastPublished = useRef(null);
  fieldRef.current = fieldState;
  ringKeyRef.current = activeRingKey;

  const [center, setCenter] = useState(DEFAULT_CENTER);
  const fieldBoundaries = fieldState?.boundaries;

  const conflictingFieldIds = overlapConflicts.map((conflict) =>
    Number(conflict.field_id)
  );
  const referenceFields = referenceFarms.flatMap((farm) =>
    (farm.fields || [])
      .filter(
        (referenceField) =>
          referenceField?.has_boundaries &&
          Array.isArray(referenceField?.boundaries) &&
          referenceField.boundaries.length > 2 &&
          Number(referenceField.field_id) !== Number(fieldState?.id)
      )
      .map((referenceField) => ({
        ...referenceField,
        farm_id: farm.farm_id,
        farm_name: farm.farm_name,
        province: farm.province,
      }))
  );

  const copyBoundaries = (boundaries) => (boundaries || []).map((point) => ({ ...point }));
  const snapshotBoundary = () => ({ boundaries: copyBoundaries(fieldRef.current?.boundaries), ringKey: ringKeyRef.current });
  const recordUndo = (snapshot = snapshotBoundary()) => {
    undoStack.current = [...undoStack.current.slice(-49), snapshot];
    setUndoCount(undoStack.current.length);
  };
  const publishBoundaries = (boundaries) => {
    const area = window.google?.maps?.geometry?.spherical?.computeArea;
    const changes = {
      boundaries, has_boundaries: boundaries.length >= 3,
      field_boundaries_size: area ? boundaryAcres(boundaries, area) : fieldRef.current?.field_boundaries_size,
    };
    lastPublished.current = boundaries;
    fieldRef.current = { ...fieldRef.current, ...changes };
    setFieldState((current) => ({ ...current, ...changes }));
  };
  const replaceRing = (points, key = ringKeyRef.current, remember = true) => {
    const [polygon, ring] = key.split(":").map(Number);
    const otherRings = boundaryRings(fieldRef.current?.boundaries || []).filter((item) => item.polygon !== polygon || item.ring !== ring);
    const next = flattenBoundaryRings([...otherRings, { polygon, ring, points }].sort((a, b) => a.polygon - b.polygon || a.ring - b.ring));
    if (JSON.stringify(next) === JSON.stringify(fieldRef.current?.boundaries || [])) return;
    if (remember) recordUndo();
    publishBoundaries(next);
  };
  const setPath = (updater) => {
    const [polygon, ring] = ringKeyRef.current.split(":").map(Number);
    const currentPath = boundaryRings(fieldRef.current?.boundaries || []).find((item) => item.polygon === polygon && item.ring === ring)?.points || [];
    replaceRing(typeof updater === "function" ? updater(currentPath) : updater);
  };
  const projectionSnapshot = () => {
    try {
      const projection = map?.getProjection();
      const mapCenter = map?.getCenter();
      const bounds = map?.getDiv()?.getBoundingClientRect();
      if (!projection || !mapCenter || !bounds?.width || !bounds?.height) return null;
      const centerPoint = projection.fromLatLngToPoint(mapCenter);
      const scale = 2 ** map.getZoom();
      return {
        bounds,
        zoom: map.getZoom(),
        center: { lat: mapCenter.lat(), lng: mapCenter.lng() },
        project(point) {
          const world = projection.fromLatLngToPoint(new window.google.maps.LatLng(point.lat, point.lng));
          return { x: bounds.width / 2 + (world.x - centerPoint.x) * scale, y: bounds.height / 2 + (world.y - centerPoint.y) * scale };
        },
        unproject(point) {
          const location = projection.fromPointToLatLng(new window.google.maps.Point(centerPoint.x + (point.x - bounds.width / 2) / scale, centerPoint.y + (point.y - bounds.height / 2) / scale));
          return { lat: location.lat(), lng: location.lng() };
        },
      };
    } catch { return null; }
  };
  const clearHoverSoon = () => {
    window.clearTimeout(hoverTimer.current);
    if (handleDrag.current) return;
    hoverTimer.current = window.setTimeout(() => setHoveredEdge(null), 350);
  };
  const retainHover = () => window.clearTimeout(hoverTimer.current);
  const hoverNearEdge = (event, polygon = null) => {
    if (!showBoundaryControls || tracing || handleDrag.current || !event.latLng) return;
    const projection = projectionSnapshot();
    if (!projection) return;
    const pointer = projection.project({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    let closest = { distance: Infinity };
    boundaryRings(fieldRef.current?.boundaries || []).filter((ring) => polygon === null || ring.polygon === polygon).forEach((ring) => {
      const candidate = findClosestProjectedSegment(ring.points, pointer, projection.project);
      if (candidate.distance < closest.distance) closest = { ...candidate, key: `${ring.polygon}:${ring.ring}` };
    });
    if (closest.distance <= 14) {
      retainHover();
      setHoveredEdge({ key: closest.key, index: closest.index });
    } else clearHoverSoon();
  };
  const appendPrecisionPoint = (point) => {
    setEditError("");
    setPath((currentPath) => freshTrace.current ? [point] : [...currentPath, point]);
    freshTrace.current = false;
  };
  const handleMapClick = (event) => {
    if (drawing && tracing && event.latLng) appendPrecisionPoint({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    else { setDrawing(false); setHoveredEdge(null); }
  };
  const traceError = (reason) => ({
    invalid_sample: "The map moved during the trace. Draw it again.",
    too_many_samples: "The trace is too detailed. Draw a shorter, smoother outline.",
    too_few_points: "Draw around an area with at least three corners.",
    tiny_stroke: "The trace is too small on the map. Zoom in and try again.",
    self_intersection: "The trace crosses itself. Draw around the edge again.",
    too_many_vertices: "The trace is too detailed. Draw a smoother outline or use clicks.",
  }[reason] || "The trace could not be used. Try drawing again.");
  const cancelGesture = (message = "") => {
    const gesture = stroke.current;
    if (gesture) {
      stroke.current = null;
      publishBoundaries(copyBoundaries(gesture.before.boundaries));
    }
    setStrokePreview([]);
    if (message) setEditError(message);
  };
  const pointerSample = (event, projection) => {
    const x = event.clientX - projection.bounds.left, y = event.clientY - projection.bounds.top;
    return { x, y, ...projection.unproject({ x, y }) };
  };
  const projectionStillCurrent = (projection) => {
    const now = projectionSnapshot();
    return now && now.zoom === projection.zoom
      && Math.abs(now.center.lat - projection.center.lat) < 1e-10
      && Math.abs(now.center.lng - projection.center.lng) < 1e-10
      && ["left", "top", "width", "height"].every((key) => Math.abs(now.bounds[key] - projection.bounds[key]) < 0.5);
  };
  const handleTraceDown = (event) => {
    if ((event.button !== undefined && event.button !== 0) || stroke.current || savingBoundaries) return;
    const projection = projectionSnapshot();
    if (!projection) { setEditError("The map is still loading. Try again in a moment."); return; }
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const recorder = createFreehandStroke();
    const first = pointerSample(event, projection);
    recorder.add(first);
    stroke.current = { pointerId: event.pointerId, recorder, projection, first, before: snapshotBoundary(), ringKey: ringKeyRef.current, moved: false, checkpointAt: Date.now() };
    setEditError("");
    setStrokePreview([first]);
  };
  const handleTraceMove = (event) => {
    const gesture = stroke.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (!projectionStillCurrent(gesture.projection)) { cancelGesture("The map view changed. Draw the outline again."); return; }
    const point = pointerSample(event, gesture.projection);
    if (Math.hypot(point.x - gesture.first.x, point.y - gesture.first.y) >= 6) gesture.moved = true;
    gesture.recorder.add(point);
    setStrokePreview((current) => gesture.recorder.preview().length > current.length ? [...current, point] : current);
    if (gesture.moved && Date.now() - gesture.checkpointAt >= 250) {
      replaceRing(gesture.recorder.preview(), gesture.ringKey, false);
      gesture.checkpointAt = Date.now();
    }
  };
  const handleTraceUp = (event) => {
    const gesture = stroke.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault();
    if (!projectionStillCurrent(gesture.projection)) { cancelGesture("The map view changed. Draw the outline again."); return; }
    const finalPoint = pointerSample(event, gesture.projection);
    gesture.recorder.add(finalPoint);
    if (Math.hypot(finalPoint.x - gesture.first.x, finalPoint.y - gesture.first.y) >= 6) gesture.moved = true;
    stroke.current = null;
    setStrokePreview([]);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!gesture.moved) { appendPrecisionPoint(gesture.first); return; }
    const result = gesture.recorder.finish();
    if (!result.ok) {
      publishBoundaries(copyBoundaries(gesture.before.boundaries));
      setEditError(traceError(result.reason));
      return;
    }
    recordUndo(gesture.before);
    replaceRing(result.points, gesture.ringKey, false);
    freshTrace.current = false;
    setDrawing(false);
    setTracing(false);
    setHoveredEdge(null);
  };
  const startTrace = (key = activeRingKey) => {
    cancelGesture();
    const [polygon, ring] = key.split(":").map(Number);
    const selected = boundaryRings(fieldRef.current?.boundaries || []).find((item) => item.polygon === polygon && item.ring === ring);
    freshTrace.current = Boolean(selected?.points.length >= 3);
    setActiveRingKey(key);
    setDrawing(true);
    setTracing(true);
    setHoveredEdge(null);
    setEditError("");
  };
  const handlePolygonClick = (event, polygon) => {
    event.stop?.();
    event.domEvent?.stopPropagation?.();
    if (!event.latLng) return;
    if (drawing && tracing) appendPrecisionPoint({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    else hoverNearEdge(event, polygon);
  };
  const beginHandleDrag = (key, index, midpoint) => {
    retainHover();
    handleDrag.current = { key, index, midpoint, before: snapshotBoundary() };
  };
  const finishHandleDrag = (event) => {
    const drag = handleDrag.current;
    if (!drag || !event.latLng) return;
    handleDrag.current = null;
    const [polygon, ring] = drag.key.split(":").map(Number);
    const currentPath = boundaryRings(fieldRef.current?.boundaries || []).find((item) => item.polygon === polygon && item.ring === ring)?.points || [];
    const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    const next = drag.midpoint
      ? [...currentPath.slice(0, drag.index + 1), point, ...currentPath.slice(drag.index + 1)]
      : currentPath.map((vertex, index) => index === drag.index ? point : vertex);
    recordUndo(drag.before);
    replaceRing(next, drag.key, false);
    setActiveRingKey(drag.key);
    setDrawing(true);
    setTracing(false);
    setHoveredEdge({ key: drag.key, index: drag.index });
  };
  const removeHandleVertex = (event, key, index) => {
    event.stop?.();
    event.domEvent?.preventDefault?.();
    const [polygon, ring] = key.split(":").map(Number);
    const selected = boundaryRings(fieldRef.current?.boundaries || []).find((item) => item.polygon === polygon && item.ring === ring)?.points || [];
    if (selected.length <= 3) { setEditError("A ring needs three vertices. Use Remove selected ring to delete it."); return; }
    replaceRing(removeVertexAtIndex(selected, index), key);
    setHoveredEdge(null);
  };
  const handleReset = () => {
    cancelGesture();
    if (!(fieldRef.current?.boundaries || []).length) return;
    recordUndo();
    publishBoundaries([]);
    setActiveRingKey("0:0");
    freshTrace.current = false;
    setDrawing(false);
    setTracing(false);
    setHoveredEdge(null);
    setEditError("");
  };
  const handleUndo = () => {
    if (stroke.current) { cancelGesture(); return; }
    const previous = undoStack.current.pop();
    if (!previous) return;
    publishBoundaries(copyBoundaries(previous.boundaries));
    setActiveRingKey(previous.ringKey);
    setUndoCount(undoStack.current.length);
    setDrawing(false);
    setTracing(false);
    setHoveredEdge(null);
    setEditError("");
  };
  const handleSave = () => {
    const currentBoundaries = fieldRef.current?.boundaries || [];
    const currentRings = boundaryRings(currentBoundaries);
    if (currentRings.length && currentRings.every((ring) => ring.points.length > 2) && window.google?.maps?.geometry?.spherical) {
      const changes = { has_boundaries: true, boundaries: currentBoundaries, field_boundaries_size: boundaryAcres(currentBoundaries, window.google.maps.geometry.spherical.computeArea) };
      setFieldState((current) => ({ ...current, ...changes }));
      setDrawing(false);
      setTracing(false);
      setHoveredEdge(null);
      setEditError("");
      onSaveBoundaries?.(changes);
    } else setEditError("Complete each ring with at least three vertices before saving.");
  };

  const mapOptions = {
    draggable: !tracing,
    scrollwheel: !tracing,
    disableDoubleClickZoom: tracing,
    keyboardShortcuts: !tracing,
    gestureHandling: tracing ? "none" : "auto",
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    rotateControl: false,
    zoomControl: !tracing,
    mapTypeId: "hybrid", // Set default to satellite view
    tilt: 0,
  };

  useEffect(() => {
    setActiveRingKey("0:0");
    setDrawing(false);
    setTracing(false);
    setRingMenuAnchor(null);
    stroke.current = null;
    undoStack.current = [];
    handleDrag.current = null;
    lastPublished.current = null;
    freshTrace.current = false;
    setUndoCount(0);
    setStrokePreview([]);
    setHoveredEdge(null);
    setEditError("");
  }, [fieldKey]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    if (map) {
      if (center.lat === DEFAULT_CENTER.lat && center.lng === DEFAULT_CENTER.lng) {
        map.setZoom(4);
      } else {
        map.setZoom(16);
      }

      map.panTo(center);
    }
  }, [map, center]);

  useEffect(() => {
    let isCurrent = true;

    async function fetchAddress() {
      if (drawing || fieldBoundaries === lastPublished.current) return;
      const preferredCenter = resolvePreferredMapCenter(
        { boundaries: fieldBoundaries },
        searchLocation
      );

      if (preferredCenter) {
        setCenter(preferredCenter);
        return;
      }

      if (
        GOOGLE_APIKEY &&
        !isTypingAddress &&
        address.trim().toLowerCase() !== "canada"
      ) {
        try {
          const data = await geocodeAddress(address);
          if (isCurrent) {
            setCenter(data);
          }
        } catch {
          if (isCurrent) {
            setCenter(DEFAULT_CENTER);
          }
        }
      }
    }

    fetchAddress();

    return () => {
      isCurrent = false;
    };
  }, [
    address,
    GOOGLE_APIKEY,
    isTypingAddress,
    fieldBoundaries,
    fieldState?.has_boundaries,
    searchLocation,
    drawing,
  ]);

  const showBoundaryControls =
    drawFieldBoundaries === true || drawFieldBoundaries === "true";

  useEffect(() => {
    onBoundaryToolbarReady?.(Boolean(GOOGLE_APIKEY && map && showBoundaryControls));
    return () => onBoundaryToolbarReady?.(false);
  }, [GOOGLE_APIKEY, map, showBoundaryControls, onBoundaryToolbarReady]);

  useEffect(() => {
    if (!showBoundaryControls) { setDrawing(false); setTracing(false); setHoveredEdge(null); }
  }, [showBoundaryControls]);

  const hoverRing = hoveredEdge ? rings.find((ring) => `${ring.polygon}:${ring.ring}` === hoveredEdge.key) : null;
  const handleEdge = hoverRing?.points.length > 2 ? hoveredEdge.index % hoverRing.points.length : -1;
  let edgeMidpoint = null;
  if (handleEdge >= 0) {
    const start = hoverRing.points[handleEdge], end = hoverRing.points[(handleEdge + 1) % hoverRing.points.length];
    const projection = projectionSnapshot();
    if (projection) {
      const a = projection.project(start), b = projection.project(end);
      edgeMidpoint = projection.unproject({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    } else edgeMidpoint = { lat: (start.lat + end.lat) / 2, lng: (start.lng + end.lng) / 2 };
  }
  const handleIcon = (midpoint) => {
    const size = midpoint ? 10 : 13;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"><circle cx="8" cy="8" r="6" fill="${midpoint ? "white" : "#F15D22"}" stroke="${midpoint ? "#F15D22" : "white"}" stroke-width="2"/></svg>`)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2),
    };
  };

  if (!GOOGLE_APIKEY) return null;

  return (
    <LoadScript
      id="google-map-script"
      googleMapsApiKey={GOOGLE_APIKEY}
      libraries={GOOGLE_MAP_LIBRARIES}
      onError={() => setMap(null)}
    >
      <div style={{ ...containerStyle, position: "relative" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={10}
        onClick={handleMapClick}
        onMouseMove={(event) => hoverNearEdge(event)}
        onMouseOut={clearHoverSoon}
        onLoad={(map) => {
          setMap(map);
        }}
        onUnmount={() => setMap(null)}
        options={mapOptions}
      >
        {window.google ? (
          <>
            {boundaryPolygonPaths(fieldState?.boundaries || []).map((paths, index) => (
              <Polygon key={index} paths={paths}
                options={drawing ? DRAWING_POLYGON_OPTIONS : SAVED_POLYGON_OPTIONS}
                onClick={(event) => handlePolygonClick(event, index)}
                onMouseOver={(event) => hoverNearEdge(event, index)}
                onMouseMove={(event) => hoverNearEdge(event, index)}
                onMouseOut={clearHoverSoon} />
            ))}
            {drawing && path.length === 2 ? (
              <Polyline
                path={path}
                options={DRAWING_POLYLINE_OPTIONS}
                onClick={(event) => handlePolygonClick(event, activePolygon)}
              />
            ) : null}
            {hoverRing && handleEdge >= 0 ? <>
              {[...new Set([handleEdge, (handleEdge + 1) % hoverRing.points.length])].map((index) => <Marker
                key={`vertex:${hoveredEdge.key}:${index}`}
                title={`Drag boundary vertex ${index + 1}`}
                position={hoverRing.points[index]}
                draggable
                zIndex={200}
                onMouseOver={retainHover}
                onMouseOut={clearHoverSoon}
                onDragStart={() => beginHandleDrag(hoveredEdge.key, index, false)}
                onDragEnd={finishHandleDrag}
                onClick={(event) => { event.stop?.(); event.domEvent?.stopPropagation?.(); }}
                onRightClick={(event) => removeHandleVertex(event, hoveredEdge.key, index)}
                icon={handleIcon(false)}
              />)}
              <Marker
                key={`midpoint:${hoveredEdge.key}:${handleEdge}`}
                title="Drag edge midpoint to add a vertex"
                position={edgeMidpoint}
                draggable
                zIndex={199}
                onMouseOver={retainHover}
                onMouseOut={clearHoverSoon}
                onDragStart={() => beginHandleDrag(hoveredEdge.key, handleEdge, true)}
                onDragEnd={finishHandleDrag}
                onClick={(event) => { event.stop?.(); event.domEvent?.stopPropagation?.(); }}
                icon={handleIcon(true)}
              />
            </> : null}
          </>
        ) : null}
        {referenceFields.map((referenceField) => {
          const isConflict = conflictingFieldIds.includes(referenceField.field_id);

          return (
            boundaryPolygonPaths(referenceField.boundaries).map((paths, index) => <Polygon
              key={`${referenceField.field_id}:${index}`}
              paths={paths}
              options={
                isConflict
                  ? CONFLICT_POLYGON_OPTIONS
                  : REFERENCE_POLYGON_OPTIONS
              }
              onMouseOver={() => {
                setHoveredReferenceField(referenceField);
              }}
              onMouseOut={() => {
                setHoveredReferenceField((currentField) =>
                  currentField?.field_id === referenceField.field_id
                    ? null
                    : currentField
                );
              }}
            />)
          );
        })}
        {hoveredReferenceField ? (
          <InfoWindow
            position={
              hoveredReferenceField.centroid ||
              hoveredReferenceField.boundaries?.[0] ||
              center
            }
            onCloseClick={() => setHoveredReferenceField(null)}
          >
            <div className="min-w-[180px] text-[#243245]">
              <p className="font-semibold">{hoveredReferenceField.field_name}</p>
              <p className="text-sm">{hoveredReferenceField.farm_name}</p>
              <p className="text-xs text-[#666666]">
                {hoveredReferenceField.province || "Province unknown"}
              </p>
              <p className="mt-1 text-xs text-[#666666]">
                {hoveredReferenceField.field_boundaries_size
                  ? `${formatAcres(
                      hoveredReferenceField.field_boundaries_size
                    )} acres`
                  : "Boundary size unavailable"}
              </p>
            </div>
          </InfoWindow>
        ) : null}
      </GoogleMap>

      {showBoundaryControls && tracing ? <div
        data-testid="boundary-trace-surface"
        role="region"
        aria-label="Trace boundary on map"
        tabIndex={0}
        onPointerDown={handleTraceDown}
        onPointerMove={handleTraceMove}
        onPointerUp={handleTraceUp}
        onPointerCancel={(event) => { if (event.pointerId === stroke.current?.pointerId) cancelGesture(); }}
        onLostPointerCapture={(event) => { if (event.pointerId === stroke.current?.pointerId) cancelGesture(); }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            cancelGesture();
            setDrawing(false);
            setTracing(false);
          } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
            event.preventDefault();
            handleUndo();
          }
        }}
        style={{ position: "absolute", inset: 0, zIndex: 2, cursor: "crosshair", touchAction: "none", outlineOffset: -2 }}
      >
        {strokePreview.length ? <svg aria-hidden="true" style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
          <polyline points={strokePreview.map((point) => `${point.x},${point.y}`).join(" ")} fill="none" stroke="#F15D22" strokeWidth="2" />
          {strokePreview.length > 2 && <line x1={strokePreview[strokePreview.length - 1].x} y1={strokePreview[strokePreview.length - 1].y} x2={strokePreview[0].x} y2={strokePreview[0].y} stroke="#F15D22" strokeDasharray="4 4" />}
        </svg> : null}
      </div> : null}
      {editError && showBoundaryControls ? <div role="alert" style={{ position: "absolute", top: 66, left: 15, zIndex: 4, maxWidth: "min(360px, calc(100% - 30px))", padding: "6px 10px", borderRadius: 4, background: "#fff4e5", color: "#663c00", fontSize: 13 }}>{editError}</div> : null}

      {showBoundaryControls ? <div
        role="region"
        aria-label="Boundary editing tools"
        style={{
          position: "absolute",
          top: 15,
          left: 15,
          zIndex: 4,
          width: "max-content",
          maxWidth: "calc(100% - 30px)",
          maxHeight: "calc(100% - 30px)",
          overflowY: "auto",
          background: "#FFFFFF",
          borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
        <IconButton aria-label="Draw boundaries" aria-pressed={tracing} onClick={() => { if (tracing) { cancelGesture(); setTracing(false); setDrawing(false); } else startTrace(); }}>
          <div
            className={`${
              !drawing && path.length === 0 ? "animate-bounce" : ""
            }  ${drawing && "text-[#F15D22]"}`}
          >
            <Tooltip title="Draw boundary: drag to trace or click to place vertices">
              <PolylineIcon />
            </Tooltip>
          </div>
        </IconButton>
        <Divider orientation="vertical" variant="middle" flexItem />
        <IconButton aria-label="Undo boundary edit" disabled={!undoCount && !stroke.current} onClick={handleUndo}>
          <Tooltip title="Undo">
            <UndoIcon />
          </Tooltip>
        </IconButton>
        <Divider orientation="vertical" variant="middle" flexItem />
        <IconButton aria-label="Clear boundaries" onClick={handleReset}>
          <Tooltip title="Clear Boundaries">
            <ClearIcon />
          </Tooltip>
        </IconButton>
        <Divider orientation="vertical" variant="middle" flexItem />

        <Tooltip title="Upload boundary (GeoJSON or KML)">
          <span><IconButton aria-label="Upload boundary" disabled={importDisabled || savingBoundaries || !onImportBoundary} onClick={() => { cancelGesture(); setTracing(false); setDrawing(false); onImportBoundary?.(); }}><FileUploadOutlinedIcon /></IconButton></span>
        </Tooltip>
        <Divider orientation="vertical" variant="middle" flexItem />

        <IconButton
          aria-label="Save boundaries"
          disabled={savingBoundaries || Boolean(stroke.current)}
          onClick={handleSave}
        >
          <div
            className={`${drawing && path.length > 2 ? "text-[#F15D22]" : ""}`}
          >
            <Tooltip
              title={savingBoundaries ? "Saving boundaries" : "Save boundaries"}
            >
              <SaveIcon />
            </Tooltip>
          </div>
        </IconButton>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Tooltip title={`Select boundary ring: Piece ${activePolygon + 1}${activeRing ? ` · Exclusion ${activeRing}` : " · Outer edge"}`}>
          <IconButton aria-label="Select boundary ring" aria-haspopup="menu" aria-expanded={Boolean(ringMenuAnchor)} onClick={(event) => setRingMenuAnchor(event.currentTarget)}>
            <LayersOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={ringMenuAnchor} open={Boolean(ringMenuAnchor)} onClose={() => setRingMenuAnchor(null)}>
          {(rings.some((ring) => `${ring.polygon}:${ring.ring}` === activeRingKey) ? rings : [...rings, { polygon: activePolygon, ring: activeRing, points: [] }]).map(({ polygon, ring, points }) =>
            <MenuItem key={`${polygon}:${ring}`} selected={`${polygon}:${ring}` === activeRingKey} onClick={() => {
              setActiveRingKey(`${polygon}:${ring}`);
              setTracing(false);
              setDrawing(true);
              setHoveredEdge({ key: `${polygon}:${ring}`, index: 0 });
              setRingMenuAnchor(null);
            }}>Piece {polygon + 1}{ring ? ` · Exclusion ${ring}` : " · Outer edge"}</MenuItem>)}
        </Menu>
        <Tooltip title="Add separate piece: draw disconnected land">
          <IconButton aria-label="Add separate piece" onClick={() => {
            startTrace(`${Math.max(-1, ...rings.map((ring) => ring.polygon)) + 1}:0`);
          }}><AddBoxOutlinedIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Add exclusion: draw inside the selected piece">
          <span><IconButton aria-label="Add exclusion" disabled={!rings.some((ring) => ring.polygon === activePolygon && ring.ring === 0 && ring.points.length >= 3)} onClick={() => {
            startTrace(`${activePolygon}:${Math.max(0, ...rings.filter((ring) => ring.polygon === activePolygon).map((ring) => ring.ring)) + 1}`);
          }}><RemoveCircleOutlineIcon /></IconButton></span>
        </Tooltip>
        <Tooltip title="Remove selected ring">
          <span><IconButton aria-label="Remove selected ring" disabled={!path.length} onClick={() => {
            cancelGesture();
            recordUndo();
            publishBoundaries(flattenBoundaryRings(rings.filter((ring) => activeRing === 0 ? ring.polygon !== activePolygon : !(ring.polygon === activePolygon && ring.ring === activeRing))));
            setActiveRingKey("0:0");
            setTracing(false);
            setDrawing(false);
            setHoveredEdge(null);
          }}><DeleteOutlineIcon /></IconButton></span>
        </Tooltip>
        </div>
      </div> : null}
      </div>
    </LoadScript>
  );
};

export default MapComponent;
